import { useTranslate } from "@similarweb/i18n-provider";
import type { IconName } from "@similarweb/icons";
import { FlexColumn, IconButton } from "@similarweb/ui-components";

import BannerIcon from "app/pages/sales-intelligence/common-components/BannerIcon/BannerIcon";
import SuccessBannerRow from "app/pages/sales-intelligence/common-components/feature-upgrade/components/SuccessBanner/SuccessBannerRow";
import type { SignalType } from "app/pages/sales-intelligence/domain/signals/types/signal";
import { featureHookKeys } from "app/pages/sales-intelligence/utils/i18n/feature-hook";

import {
    StyledSignalUpsellBannerButton,
    StyledSignalUpsellBannerContent,
    StyledSignalUpsellBannerSubtitle,
    StyledSignalUpsellBannerTitle,
    StyledSignalUpsellBannerWrapper,
} from "./styles";

type SignalUpsellBannerProps = {
    signalType: SignalType;
    title: string;
    description: string;
    ctaLabel: string;
    onRequestAccess: VoidFunction;
    iconName?: IconName;
    iconImagePath?: string;
    isRequestSent?: boolean;
};

/** The full-width upsell row a locked signal type gets, in the company-analysis feed and on the management page alike. */
export const SignalUpsellBanner = ({
    signalType,
    title,
    description,
    ctaLabel,
    onRequestAccess,
    iconName,
    iconImagePath,
    isRequestSent = false,
}: SignalUpsellBannerProps) => {
    const { translate } = useTranslate();
    const automationPrefix = `signal-promo-banner-${signalType}`;

    if (isRequestSent) {
        return (
            <div data-automation={`${automationPrefix}-success`}>
                <SuccessBannerRow
                    title={translate(featureHookKeys.modalSuccessTitle)}
                    subTitle={translate(featureHookKeys.modalSuccessSubtitle)}
                />
            </div>
        );
    }

    return (
        <StyledSignalUpsellBannerWrapper data-automation={automationPrefix}>
            <BannerIcon
                iconName={iconName}
                iconImagePath={iconImagePath}
            />
            <StyledSignalUpsellBannerContent>
                <FlexColumn>
                    <StyledSignalUpsellBannerTitle data-automation={`${automationPrefix}-title`}>
                        {title}
                    </StyledSignalUpsellBannerTitle>
                    <StyledSignalUpsellBannerSubtitle data-automation={`${automationPrefix}-subtitle`}>
                        {description}
                    </StyledSignalUpsellBannerSubtitle>
                </FlexColumn>

                <StyledSignalUpsellBannerButton data-automation={`${automationPrefix}-button`}>
                    <IconButton
                        type="trial"
                        onClick={onRequestAccess}
                        iconName="sparkles"
                    >
                        {ctaLabel}
                    </IconButton>
                </StyledSignalUpsellBannerButton>
            </StyledSignalUpsellBannerContent>
        </StyledSignalUpsellBannerWrapper>
    );
};
